import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";

interface DayActivity {
  day: string;
  minutes: number;
}

interface ActivityTrendChartProps {
  data?: DayActivity[];
  avgHours?: number;
  bare?: boolean;
}

const TEAL = "#20BCBB";

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value ?? 0;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[8px] px-3 py-2 shadow-md text-xs font-inter">
      <p className="font-semibold text-[#1F1F1F] mb-1 text-[10px] tracking-wide">
        {String(label).slice(0, 2)}
      </p>
      <div className="flex items-center gap-1.5 mt-0.5">
        <span
          className="w-2 h-2 rounded-full inline-block flex-shrink-0"
          style={{ background: TEAL }}
        />
        <span style={{ color: TEAL }} className="font-medium">
          Activity · {value != null ? `${value} mins` : "—"}
        </span>
      </div>
    </div>
  );
};

function getYAxis(data: DayActivity[], targetTickCount = 5) {
  const values = data.map((d) => d.minutes).filter((v): v is number => v != null);
  if (values.length === 0) {
    return { domain: [0, 80] as [number, number], ticks: [0, 20, 40, 60, 80] };
  }

  const rawMax = Math.max(...values, 0);
  if (rawMax <= 0) {
    return { domain: [0, 80] as [number, number], ticks: [0, 20, 40, 60, 80] };
  }

  // Nice minute-steps so tick labels stay clean and the peak always fits.
  const NICE_STEPS = [10, 20, 30, 60, 120, 240, 480];
  const targetStep = rawMax / (targetTickCount - 1);
  const step =
    NICE_STEPS.find((s) => s >= targetStep) ?? NICE_STEPS[NICE_STEPS.length - 1];

  let domainMax = Math.ceil(rawMax / step) * step;
  while (domainMax / step < targetTickCount - 1) domainMax += step;

  const ticks: number[] = [];
  for (let v = 0; v <= domainMax + 1e-9; v += step) ticks.push(v);

  return { domain: [0, domainMax] as [number, number], ticks };
}

const ActivityTrendChart: React.FC<ActivityTrendChartProps> = ({
  data = [],
  avgHours,
  bare = false,
}) => {
  const { domain, ticks } = getYAxis(data);
  const chart = (
    <ResponsiveContainer width="100%" height={140}>
      <LineChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#F3F4F6" strokeDasharray="0" />
        <XAxis
          dataKey="day"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
          dy={6}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
          domain={domain}
          ticks={ticks}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#E5E7EB", strokeDasharray: "3 3" }} />
        <Line
          type="monotone"
          dataKey="minutes"
          stroke={TEAL}
          strokeWidth={2.5}
          dot={false}
          activeDot={{ r: 5, fill: TEAL, strokeWidth: 2, stroke: "#FFFFFF" }}
          isAnimationActive={true}
          animationBegin={0}
          animationDuration={1000}
          animationEasing="ease-out"
        />
      </LineChart>
    </ResponsiveContainer>
  );

  if (bare) return chart;

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Activity Trend
        </span>
      </div>

      {/* Chart */}
      <div className="flex-1">{chart}</div>

      {/* Footer */}
      {avgHours != null && (
        <div className="text-[12px] 3xl:text-[14px] text-[#8D8D8D] font-inter text-center mt-1">
          Avg duration :{" "}
          <span className="font-semibold text-[#1F1F1F]">{avgHours} hour</span>
        </div>
      )}
    </div>
  );
};

export default ActivityTrendChart;
